import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import CameraPhotoCapture, { CameraPhotoCaptureHandle } from '../../components/visitor-logs/CameraPhotoCapture';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import Badge from '../../components/ui/Badge';
import { ArrowLeft, Upload, X, Camera as CameraIcon, Loader2, ParkingCircle, Search, CheckCircle, XCircle, User, Eye } from 'lucide-react';
import { visitorLogService, locationService, parkingService, personService } from '../../services';
import { cameraService } from '../../services/camera.service';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import type { Location, Camera, ParkingLot, ParkingSpot, FaceRecognitionResponse, Person } from '../../types';

const CreateEntryExitPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId: globalLocationId } = useLocationContext();
  const [locations, setLocations] = useState<Location[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Capture camera states
  const [selectedIdCamera, setSelectedIdCamera] = useState<string>('');
  const [selectedFaceCamera, setSelectedFaceCamera] = useState<string>('');
  const [selectedLicensePlateCamera, setSelectedLicensePlateCamera] = useState<string>('');
  const [capturedIdImage, setCapturedIdImage] = useState<string | null>(null);
  const [capturedFaceImage, setCapturedFaceImage] = useState<string | null>(null);
  const [capturedLicensePlateImage, setCapturedLicensePlateImage] = useState<string | null>(null);
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [facePhotoFile, setFacePhotoFile] = useState<File | null>(null);
  const [licensePlatePhotoFile, setLicensePlatePhotoFile] = useState<File | null>(null);

  // Vehicle angle photos
  const [vehicleFrontImage, setVehicleFrontImage] = useState<string | null>(null);
  const [vehicleRightImage, setVehicleRightImage] = useState<string | null>(null);
  const [vehicleLeftImage, setVehicleLeftImage] = useState<string | null>(null);
  const [vehicleBackImage, setVehicleBackImage] = useState<string | null>(null);
  const [vehicleFrontFile, setVehicleFrontFile] = useState<File | null>(null);
  const [vehicleRightFile, setVehicleRightFile] = useState<File | null>(null);
  const [vehicleLeftFile, setVehicleLeftFile] = useState<File | null>(null);
  const [vehicleBackFile, setVehicleBackFile] = useState<File | null>(null);
  const [isCapturingVehicleAngle, setIsCapturingVehicleAngle] = useState<string | null>(null);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [uploadingImages, setUploadingImages] = useState(false);

  // Parking state
  const [parkingLots, setParkingLots] = useState<ParkingLot[]>([]);
  const [availableSpots, setAvailableSpots] = useState<ParkingSpot[]>([]);
  const [selectedParkingLot, setSelectedParkingLot] = useState<string>('');
  const [selectedParkingSpot, setSelectedParkingSpot] = useState<string>('');
  const [selectedParkingSpot2, setSelectedParkingSpot2] = useState<string>(''); // Second spot for truck with trailer
  const [loadingParkingLots, setLoadingParkingLots] = useState(false);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [parkingSummary, setParkingSummary] = useState<{ available: number; total: number } | null>(null);

  // Face Recognition & Plate Lookup state
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognitionResult, setRecognitionResult] = useState<FaceRecognitionResponse | null>(null);
  const [recognizedPerson, setRecognizedPerson] = useState<Person | null>(null);
  const [isLookingUpPlate, setIsLookingUpPlate] = useState(false);
  const [plateLookupResult, setPlateLookupResult] = useState<Person | null | undefined>(undefined);

  // Form state - initialize location_id from global context if available
  const [formData, setFormData] = useState({
    location_id: '',
    log_access_type: 'ENTRY',
    log_operator_type: 'EMPLOYEE',
    log_capture_type: 'MANUAL',
    has_vehicle: '1',
    log_license_plate: '',
    log_vehicle_type: '',
    log_vehicle_make: '',
    log_vehicle_color: '',
    log_vehicle_model: '',
    log_truck: '',
    log_trailer: '',
    log_trailer_plate: '',
    log_damages: '',
    log_safety_vest: '',
    log_external_driver_name: '',
    log_driver_license_no: '',
    log_driver_email: '',
    log_visitor_company: '',
    log_visitor_phone_number: '',
    log_visitor_purpose: '',
    log_note: '',
    access_point: '',
    inspection_status: '',
    inspected_by: '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchLocations();
  }, []);

  // Set location from global context when available and not 'all'
  useEffect(() => {
    if (globalLocationId && globalLocationId !== 'all' && !formData.location_id) {
      setFormData(prev => ({ ...prev, location_id: globalLocationId }));
      // Fetch cameras and parking lots for the selected location
      fetchCameras(globalLocationId);
      fetchParkingLots(globalLocationId);
    }
  }, [globalLocationId]);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const fetchCameras = async (locationId: string) => {
    if (!locationId) {
      setCameras([]);
      return;
    }
    try {
      const response = await cameraService.getCamerasByLocation(parseInt(locationId), { size: 100 });
      console.log('All cameras for location:', response.data);
      // Filter cameras by purpose: face_recognition, id_capture, license_plate, and vehicle angles
      const filtered = (response.data || []).filter(camera => 
        camera.camera_purpose === 'face_recognition' || 
        camera.camera_purpose === 'id_capture' ||
        camera.camera_purpose === 'license_plate' ||
        camera.camera_purpose === 'vehicle_front' ||
        camera.camera_purpose === 'vehicle_right' ||
        camera.camera_purpose === 'vehicle_left' ||
        camera.camera_purpose === 'vehicle_back'
      );
      console.log('Filtered cameras (id_capture, face_recognition, license_plate, vehicle angles):', filtered);
      setCameras(filtered);
    } catch (err) {
      console.error('Failed to fetch cameras:', err);
      setCameras([]);
    }
  };

  // Face Recognition - identify person from captured face photo
  const recognizeFace = async () => {
    if (!facePhotoFile) {
      setError('Please capture a face photo first');
      return;
    }

    try {
      setIsRecognizing(true);
      setRecognitionResult(null);
      setRecognizedPerson(null);

      const result = await personService.recognizeFace(facePhotoFile, {
        location_id: formData.location_id ? parseInt(formData.location_id) : undefined,
        threshold: 0.7, // 70% confidence threshold
      });

      setRecognitionResult(result);

      // If we have a match, auto-fill the form with person data
      if (result.matched && result.matches.length > 0) {
        const topMatch = result.matches[0];
        // Fetch full person details
        try {
          const person = await personService.getPersonById(topMatch.person_id);
          setRecognizedPerson(person);
          
          // Auto-fill form fields based on person type
          if (person.person_type === 'EMPLOYEE') {
            handleInputChange('log_operator_type', 'EMPLOYEE');
          } else if (person.person_type === 'SUBCONTRACTOR') {
            handleInputChange('log_operator_type', 'SUBCONTRACTOR');
            if (person.external_company) {
              handleInputChange('log_visitor_company', person.external_company);
            }
          } else if (person.person_type === 'VISITOR') {
            handleInputChange('log_operator_type', 'VISITOR');
            if (person.external_company) {
              handleInputChange('log_visitor_company', person.external_company);
            }
          }
          
          // Fill driver name
          handleInputChange('log_external_driver_name', `${person.first_name} ${person.last_name}`);
          
          // Fill contact info if available
          if (person.email) {
            handleInputChange('log_driver_email', person.email);
          }
          if (person.phone) {
            handleInputChange('log_visitor_phone_number', person.phone);
          }
          if (person.employee_id) {
            handleInputChange('log_driver_license_no', person.employee_id);
          }
        } catch (personErr) {
          console.error('Failed to fetch person details:', personErr);
        }
      }
    } catch (err) {
      console.error('Face recognition failed:', err);
      setError(err instanceof Error ? err.message : 'Face recognition failed');
    } finally {
      setIsRecognizing(false);
    }
  };

  // Plate Lookup - find person by license plate
  const lookupPlate = async () => {
    const plate = formData.log_license_plate.trim();
    if (!plate) {
      setError('Please enter a license plate number');
      return;
    }

    try {
      setIsLookingUpPlate(true);
      setPlateLookupResult(undefined);
      setRecognizedPerson(null);

      const result = await personService.lookupByPlate(plate);
      setPlateLookupResult(result);

      // If found, auto-fill form with person data
      if (result) {
        setRecognizedPerson(result);
        
        // Auto-fill similar to face recognition
        if (result.person_type === 'EMPLOYEE') {
          handleInputChange('log_operator_type', 'EMPLOYEE');
        } else if (result.person_type === 'SUBCONTRACTOR') {
          handleInputChange('log_operator_type', 'SUBCONTRACTOR');
          if (result.external_company) {
            handleInputChange('log_visitor_company', result.external_company);
          }
        } else if (result.person_type === 'VISITOR') {
          handleInputChange('log_operator_type', 'VISITOR');
          if (result.external_company) {
            handleInputChange('log_visitor_company', result.external_company);
          }
        }
        
        handleInputChange('log_external_driver_name', `${result.first_name} ${result.last_name}`);
        
        if (result.email) {
          handleInputChange('log_driver_email', result.email);
        }
        if (result.phone) {
          handleInputChange('log_visitor_phone_number', result.phone);
        }
        if (result.employee_id) {
          handleInputChange('log_driver_license_no', result.employee_id);
        }
      }
    } catch (err) {
      console.error('Plate lookup failed:', err);
      setError(err instanceof Error ? err.message : 'Plate lookup failed');
    } finally {
      setIsLookingUpPlate(false);
    }
  };

  // Clear recognized person
  const clearRecognizedPerson = () => {
    setRecognizedPerson(null);
    setRecognitionResult(null);
    setPlateLookupResult(undefined);
  };

  const licensePlateCaptureRef = useRef<CameraPhotoCaptureHandle>(null);
  const captureUrls = useRef<Record<string, string>>({});
  useEffect(() => () => {
    Object.values(captureUrls.current).forEach(url => URL.revokeObjectURL(url));
  }, []);

  const photoSetters = {
    id: [setCapturedIdImage, setIdPhotoFile] as const,
    face: [setCapturedFaceImage, setFacePhotoFile] as const,
    license_plate: [setCapturedLicensePlateImage, setLicensePlatePhotoFile] as const,
    front: [setVehicleFrontImage, setVehicleFrontFile] as const,
    right: [setVehicleRightImage, setVehicleRightFile] as const,
    left: [setVehicleLeftImage, setVehicleLeftFile] as const,
    back: [setVehicleBackImage, setVehicleBackFile] as const,
  };
  const applyCapture = (type: keyof typeof photoSetters, file: File) => {
    if (captureUrls.current[type]) URL.revokeObjectURL(captureUrls.current[type]);
    const url = URL.createObjectURL(file);
    captureUrls.current[type] = url;
    const [setImage, setFile] = photoSetters[type];
    setImage(url); setFile(file);
    if (type === 'face') setRecognitionResult(null);
  };
  const captureVehicleAngle = async (angle: 'front' | 'right' | 'left' | 'back') => {
    if (!selectedLicensePlateCamera || !licensePlateCaptureRef.current) {
      setError('Select a license plate camera and wait for its preview first.');
      return;
    }
    setIsCapturingVehicleAngle(angle); setError(null);
    try {
      const blob = await licensePlateCaptureRef.current.capture();
      applyCapture(angle, new File([blob], `vehicle-${angle}-${Date.now()}.jpg`, { type: blob.type }));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to capture this vehicle photo.');
    } finally { setIsCapturingVehicleAngle(null); }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear license plate data when "Has Vehicle" is unchecked
    if (field === 'has_vehicle' && value === '0') {
      setSelectedLicensePlateCamera('');
      setCapturedLicensePlateImage(null);
      setLicensePlatePhotoFile(null);


    }
    
    // Fetch cameras and parking lots when location changes
    if (field === 'location_id') {
      fetchCameras(value);
      fetchParkingLots(value);
      // Reset camera selections when location changes
      setSelectedIdCamera('');
      setSelectedFaceCamera('');
      setSelectedLicensePlateCamera('');
      setCapturedIdImage(null);
      setCapturedFaceImage(null);
      setCapturedLicensePlateImage(null);
      setIdPhotoFile(null);
      setFacePhotoFile(null);
      setLicensePlatePhotoFile(null);






      // Reset parking selections
      setSelectedParkingLot('');
      setSelectedParkingSpot('');
      setSelectedParkingSpot2('');
      setAvailableSpots([]);
      setParkingSummary(null);
    }
    
    // Refresh spots when vehicle type changes
    if (field === 'log_vehicle_type' && selectedParkingLot) {
      fetchSpotsForAccessType(selectedParkingLot, value);
      // Clear second spot if vehicle type is no longer truck with trailer
      if (value !== 'Truck with Trailer') {
        setSelectedParkingSpot2('');
      }
    }
    
    // Refresh spots when access type changes (ENTRY shows available, EXIT shows occupied)
    if (field === 'log_access_type' && selectedParkingLot) {
      // Pass the new access type value directly since formData won't be updated yet
      setSelectedParkingSpot(''); // Clear selected spot when switching access type
      setSelectedParkingSpot2(''); // Clear second spot too
      fetchSpotsForAccessType(selectedParkingLot, formData.log_vehicle_type, value);
    }
    
    // Clear validation error when field is modified
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Fetch parking lots for a location
  const fetchParkingLots = async (locationId: string) => {
    if (!locationId) {
      setParkingLots([]);
      return;
    }
    try {
      setLoadingParkingLots(true);
      const response = await parkingService.getLots(parseInt(locationId));
      console.log('Parking lots for entry/exit:', response);
      // Handle both direct array response and wrapped {data: [...]} response
      const lots = Array.isArray(response) ? response : (response.data || []);
      setParkingLots(lots);
    } catch (err) {
      console.error('Failed to fetch parking lots:', err);
      setParkingLots([]);
    } finally {
      setLoadingParkingLots(false);
    }
  };

  // Fetch spots for a parking lot - available for ENTRY, occupied for EXIT
  const fetchSpotsForAccessType = async (lotId: string, vehicleType?: string, accessType?: string) => {
    if (!lotId) {
      setAvailableSpots([]);
      return;
    }
    try {
      setLoadingSpots(true);
      
      // Use the passed accessType or fall back to formData
      const currentAccessType = accessType || formData.log_access_type;
      
      if (currentAccessType === 'EXIT') {
        // For EXIT, fetch all spots and filter to occupied ones
        const response = await parkingService.getSpots(parseInt(lotId));
        console.log('All spots response for EXIT:', response);
        const allSpots = Array.isArray(response) ? response : (response.data || []);
        // Filter to only occupied spots
        const occupiedSpots = allSpots.filter((spot: any) => spot.status === 'occupied');
        console.log('Occupied spots for EXIT:', occupiedSpots);
        setAvailableSpots(occupiedSpots);
      } else {
        // For ENTRY, fetch available spots
        const spotType = vehicleType ? parkingService.getSpotTypeForVehicle(vehicleType) : undefined;
        const response = await parkingService.getAvailableSpots(parseInt(lotId), spotType);
        console.log('Available spots response for ENTRY:', response);
        const spots = Array.isArray(response) ? response : (response.data || []);
        setAvailableSpots(spots);
      }
      
      // Also get summary for display
      try {
        const summaryResponse = await parkingService.getSummary(parseInt(lotId));
        const summary = (summaryResponse as any).data || summaryResponse;
        setParkingSummary({ available: summary.available || summary.available_spots || 0, total: summary.total_spots || 0 });
      } catch {
        // Summary is optional
      }
    } catch (err) {
      console.error('Failed to fetch spots:', err);
      setAvailableSpots([]);
    } finally {
      setLoadingSpots(false);
    }
  };

  // Handle parking lot selection
  const handleParkingLotChange = (lotId: string) => {
    setSelectedParkingLot(lotId);
    setSelectedParkingSpot('');
    setSelectedParkingSpot2('');
    if (lotId) {
      fetchSpotsForAccessType(lotId, formData.log_vehicle_type);
    } else {
      setAvailableSpots([]);
      setParkingSummary(null);
    }
  };

  // Handle parking spot selection
  const handleParkingSpotChange = (spotId: string) => {
    setSelectedParkingSpot(spotId);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationErrors(prev => ({
        ...prev,
        photo: 'Photo size must not exceed 5MB',
      }));
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setValidationErrors(prev => ({
        ...prev,
        photo: 'Photo must be JPEG, PNG, or WEBP format',
      }));
      return;
    }

    setPhoto(file);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.photo;
      return newErrors;
    });

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const validateLicensePlate = (plate: string): boolean => {
    if (!plate.trim()) return false;
    // Matches formats like: ABC-1234, AB-123, ABCD1234, etc.
    const regex = /^[A-Z0-9]{2,3}-?[A-Z0-9]{3,4}$/i;
    return regex.test(plate);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.location_id) {
      errors.location_id = 'Location is required';
    }

    // Only require license plate if vehicle is present
    if (formData.has_vehicle === '1' && !formData.log_license_plate.trim()) {
      errors.log_license_plate = 'License plate is required';
    }

    if (formData.log_note.length > 2000) {
      errors.log_note = 'Notes must not exceed 2000 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors');
      return;
    }

    try {
      setIsLoading(true);
      setUploadingImages(true);
      setError(null);

      // Step 1: Upload all images to S3 and collect URLs
      const imageUploads: { file: File; logType: string; key: string }[] = [];
      
      if (idPhotoFile) {
        imageUploads.push({ file: idPhotoFile, logType: 'id', key: 'log_license_image' });
      }
      if (facePhotoFile) {
        imageUploads.push({ file: facePhotoFile, logType: 'face', key: 'log_image' });
      }
      if (licensePlatePhotoFile) {
        imageUploads.push({ file: licensePlatePhotoFile, logType: 'license_plate', key: 'log_url' });
      }
      if (vehicleFrontFile) {
        imageUploads.push({ file: vehicleFrontFile, logType: 'vehicle_front', key: 'log_vehicle_image_front' });
      }
      if (vehicleRightFile) {
        imageUploads.push({ file: vehicleRightFile, logType: 'vehicle_right', key: 'log_vehicle_image_right' });
      }
      if (vehicleLeftFile) {
        imageUploads.push({ file: vehicleLeftFile, logType: 'vehicle_left', key: 'log_vehicle_image_left' });
      }
      if (vehicleBackFile) {
        imageUploads.push({ file: vehicleBackFile, logType: 'vehicle_back', key: 'log_vehicle_image_back' });
      }

      const imageUrls: Record<string, string> = {};
      
      // Upload images sequentially with progress tracking
      for (let i = 0; i < imageUploads.length; i++) {
        const { file, logType, key } = imageUploads[i];
        setUploadProgress(`Uploading image ${i + 1} of ${imageUploads.length}...`);
        
        try {
          const uploadResponse = await visitorLogService.uploadImage(
            file, 
            logType as any,
            parseInt(formData.location_id)
          );
          imageUrls[key] = uploadResponse.url;
          console.log(`Uploaded ${logType}:`, uploadResponse.url);
        } catch (uploadErr) {
          console.error(`Failed to upload ${logType}:`, uploadErr);
          throw new Error(`Failed to upload ${logType} image`);
        }
      }

      setUploadingImages(false);
      setUploadProgress('Creating visitor log...');

      // Step 2: Create visitor log with image URLs
      const payload: any = {
        location_id: parseInt(formData.location_id),
        log_access_type: formData.log_access_type,
        log_operator_type: formData.log_operator_type,
        log_capture_type: formData.log_capture_type,
        has_vehicle: formData.has_vehicle,
        log_license_plate: formData.log_license_plate.toUpperCase(),
      };
      
      // Add non-vehicle image URLs to payload
      if (imageUrls.log_license_image) payload.log_license_image = imageUrls.log_license_image;
      if (imageUrls.log_image) payload.log_image = imageUrls.log_image;
      if (imageUrls.log_url) payload.log_url = imageUrls.log_url;
      
      // Add optional fields
      if (formData.log_vehicle_type) payload.log_vehicle_type = formData.log_vehicle_type;
      if (formData.log_vehicle_make) payload.log_vehicle_make = formData.log_vehicle_make;
      if (formData.log_vehicle_color) payload.log_vehicle_color = formData.log_vehicle_color;
      if (formData.log_vehicle_model) payload.log_vehicle_model = formData.log_vehicle_model;
      if (formData.log_truck) payload.log_truck = formData.log_truck.replace(/\s+/g, '').toUpperCase();
      if (formData.log_trailer) payload.log_trailer = formData.log_trailer.replace(/\s+/g, '').toUpperCase();
      if (formData.log_trailer_plate) payload.log_trailer_plate = formData.log_trailer_plate.replace(/\s+/g, '').toUpperCase();
      if (formData.log_damages) payload.log_damages = formData.log_damages;
      if (formData.log_safety_vest) payload.log_safety_vest = formData.log_safety_vest;
      if (formData.log_external_driver_name) payload.log_external_driver_name = formData.log_external_driver_name;
      if (formData.log_driver_license_no) payload.log_driver_license_no = formData.log_driver_license_no;
      if (formData.log_driver_email) payload.log_driver_email = formData.log_driver_email;
      if (formData.log_visitor_company) payload.log_visitor_company = formData.log_visitor_company;
      if (formData.log_visitor_phone_number) payload.log_visitor_phone_number = formData.log_visitor_phone_number;
      if (formData.log_visitor_purpose) payload.log_visitor_purpose = formData.log_visitor_purpose;
      if (formData.log_note) payload.log_note = formData.log_note;

      // Add vehicle inspection details as JSON (including vehicle images)
      const vehicleDetails: any = {
        access_point: formData.access_point || '',
        inspection_status: formData.inspection_status || '',
        inspected_by: formData.inspected_by || '',
      };
      
      // Add vehicle image URLs to vehicle_details
      if (imageUrls.log_vehicle_image_front) vehicleDetails.log_vehicle_image_front = imageUrls.log_vehicle_image_front;
      if (imageUrls.log_vehicle_image_right) vehicleDetails.log_vehicle_image_right = imageUrls.log_vehicle_image_right;
      if (imageUrls.log_vehicle_image_left) vehicleDetails.log_vehicle_image_left = imageUrls.log_vehicle_image_left;
      if (imageUrls.log_vehicle_image_back) vehicleDetails.log_vehicle_image_back = imageUrls.log_vehicle_image_back;
      
      console.log('Vehicle inspection fields:', vehicleDetails);
      
      // Always add vehicle_details if there's any data
      if (Object.keys(vehicleDetails).length > 0) {
        payload.vehicle_details = JSON.stringify(vehicleDetails);
        console.log('Added vehicle_details to payload:', payload.vehicle_details);
      } else {
        console.log('Skipping vehicle_details - no data');
      }

      // Add parking information for ENTRY
      if (formData.log_access_type === 'ENTRY' && selectedParkingLot && selectedParkingSpot) {
        const selectedSpot = availableSpots.find(s => {
          const sId = (s as any).id || s.spot_id;
          return sId?.toString() === selectedParkingSpot;
        });
        if (selectedSpot) {
          payload.parking_lot_id = parseInt(selectedParkingLot);
          payload.parking_spot_id = parseInt(selectedParkingSpot);
          payload.parking_spot_number = selectedSpot.spot_number;
          
          // Add second spot for truck with trailer
          if (selectedParkingSpot2) {
            const selectedSpot2 = availableSpots.find(s => {
              const sId = (s as any).id || s.spot_id;
              return sId?.toString() === selectedParkingSpot2;
            });
            if (selectedSpot2) {
              payload.parking_spot_id_2 = parseInt(selectedParkingSpot2);
              payload.parking_spot_number_2 = selectedSpot2.spot_number;
            }
          }
        }
      }

      // Add parking release information for EXIT
      if (formData.log_access_type === 'EXIT' && selectedParkingLot && selectedParkingSpot) {
        const selectedSpot = availableSpots.find(s => {
          const sId = (s as any).id || s.spot_id;
          return sId?.toString() === selectedParkingSpot;
        });
        if (selectedSpot) {
          payload.parking_lot_id = parseInt(selectedParkingLot);
          payload.parking_spot_id = parseInt(selectedParkingSpot);
          payload.parking_spot_number = selectedSpot.spot_number;
          
          // Add second spot for truck with trailer
          if (selectedParkingSpot2) {
            const selectedSpot2 = availableSpots.find(s => {
              const sId = (s as any).id || s.spot_id;
              return sId?.toString() === selectedParkingSpot2;
            });
            if (selectedSpot2) {
              payload.parking_spot_id_2 = parseInt(selectedParkingSpot2);
              payload.parking_spot_number_2 = selectedSpot2.spot_number;
            }
          }
        }
      }

      console.log('=== CREATE VISITOR LOG PAYLOAD ===');
      console.log(JSON.stringify(payload, null, 2));
      console.log('=== END PAYLOAD ===');

      const response = await visitorLogService.createVisitorLog(payload);

      // Mark parking spot as occupied for ENTRY
      // Mark parking spot as occupied for ENTRY
      if (formData.log_access_type === 'ENTRY' && selectedParkingSpot && selectedParkingLot) {
        try {
          const isTruckWithTrailer = formData.log_vehicle_type === 'Truck with Trailer';
          const isTruck = formData.log_vehicle_type === 'Truck Only' || isTruckWithTrailer;
          const trailerPlate = formData.log_trailer_plate ? formData.log_trailer_plate.toUpperCase() : undefined;
          
          if (isTruckWithTrailer && selectedParkingSpot2) {
            // For truck with trailer with 2 spots - use multi-spot occupy endpoint
            await parkingService.occupyMultipleSpots(parseInt(selectedParkingLot), {
              vehicle_plate: formData.log_license_plate.toUpperCase(),
              trailer_plate: trailerPlate,
              vehicle_type: 'truck',
              driver_name: formData.log_external_driver_name || undefined,
              spot_ids: [parseInt(selectedParkingSpot), parseInt(selectedParkingSpot2)]
            });
          } else if (isTruck) {
            // For truck only - single spot
            await parkingService.occupyMultipleSpots(parseInt(selectedParkingLot), {
              vehicle_plate: formData.log_license_plate.toUpperCase(),
              trailer_plate: trailerPlate,
              vehicle_type: 'truck',
              driver_name: formData.log_external_driver_name || undefined,
              spot_ids: [parseInt(selectedParkingSpot)]
            });
          } else {
            // For cars/motorcycles, use single spot park endpoint
            await parkingService.parkInSpot(parseInt(selectedParkingSpot), {
              vehicle_plate: formData.log_license_plate.toUpperCase(),
              vehicle_type: 'car',
              driver_name: formData.log_external_driver_name || undefined
            });
          }
          console.log('Parking spot(s) marked as occupied');
        } catch (parkingErr) {
          console.error('Failed to update parking spot status:', parkingErr);
          // Don't fail the entire operation, just log the error
        }
      }

      // Free parking spot for EXIT - just send spot_ids, backend reads plates from spots
      if (formData.log_access_type === 'EXIT' && selectedParkingSpot && selectedParkingLot) {
        try {
          const spotIds = [parseInt(selectedParkingSpot)];
          if (selectedParkingSpot2) {
            spotIds.push(parseInt(selectedParkingSpot2));
          }
          
          // Use multi-spot release endpoint
          await parkingService.releaseMultipleSpots(parseInt(selectedParkingLot), {
            spot_ids: spotIds
          });
          console.log('Parking spot(s) freed');
        } catch (parkingErr) {
          console.error('Failed to free parking spot:', parkingErr);
          // Don't fail the entire operation, just log the error
        }
      }

      navigate(`/entry-exit/${response.log_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create log');
    } finally {
      setIsLoading(false);
      setUploadingImages(false);
      setUploadProgress('');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/entry-exit')}
            leftIcon={<ArrowLeft size={18} />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">New Entry/Exit Log</h1>
            <p className="text-sm text-text-muted mt-1">Record a new vehicle entry or exit</p>
          </div>
        </div>

        {error && <Alert variant="error" title="Error" message={error} />}

        {uploadingImages && uploadProgress && (
          <Alert variant="info" title="Uploading Images" message={uploadProgress} />
        )}

        {/* Recognized Person Banner */}
        {recognizedPerson && (
          <Card className="mb-6 border-2 border-green-500/50 bg-green-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-surface-secondary overflow-hidden flex items-center justify-center">
                  {recognizedPerson.primary_photo_url ? (
                    <img
                      src={recognizedPerson.primary_photo_url}
                      alt={`${recognizedPerson.first_name} ${recognizedPerson.last_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={24} className="text-text-muted" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} className="text-green-500" />
                    <span className="font-semibold text-text-primary">
                      Person Identified
                    </span>
                  </div>
                  <p className="text-lg font-medium text-text-primary">
                    {recognizedPerson.first_name} {recognizedPerson.last_name}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-text-muted">
                    <Badge variant={
                      recognizedPerson.person_type === 'EMPLOYEE' ? 'info' :
                      recognizedPerson.person_type === 'SUBCONTRACTOR' ? 'warning' : 'success'
                    } size="sm">
                      {recognizedPerson.person_type}
                    </Badge>
                    {recognizedPerson.employee_id && <span>• {recognizedPerson.employee_id}</span>}
                    {recognizedPerson.external_company && <span>• {recognizedPerson.external_company}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  leftIcon={<Eye size={14} />}
                  onClick={() => navigate(`/persons/${recognizedPerson.id}/view`)}
                >
                  View
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearRecognizedPerson}
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.location_id}
                  onChange={e => handleInputChange('location_id', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    validationErrors.location_id
                      ? 'border-red-500'
                      : 'border-gray-300'
                  } bg-background-primary text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  <option value="">Select a location</option>
                  {locations.map(location => (
                    <option key={location.ycl_id} value={location.ycl_id}>
                      {location.ycl_name}
                    </option>
                  ))}
                </select>
                {validationErrors.location_id && (
                  <p className="text-sm text-red-500 mt-1">{validationErrors.location_id}</p>
                )}
              </div>

              {/* Access Type */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Access Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="access_type"
                      value="ENTRY"
                      checked={formData.log_access_type === 'ENTRY'}
                      onChange={e => handleInputChange('log_access_type', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Entry</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="access_type"
                      value="EXIT"
                      checked={formData.log_access_type === 'EXIT'}
                      onChange={e => handleInputChange('log_access_type', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Exit</span>
                  </label>
                </div>
              </div>

              {/* Entry/Exit By */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Entry/Exit By <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="operator_type"
                      value="EMPLOYEE"
                      checked={formData.log_operator_type === 'EMPLOYEE'}
                      onChange={e => handleInputChange('log_operator_type', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Employee</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="operator_type"
                      value="SUBCONTRACTOR"
                      checked={formData.log_operator_type === 'SUBCONTRACTOR'}
                      onChange={e => handleInputChange('log_operator_type', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Subcontractor</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="operator_type"
                      value="VISITOR"
                      checked={formData.log_operator_type === 'VISITOR'}
                      onChange={e => handleInputChange('log_operator_type', e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-text-primary">Visitor</span>
                  </label>
                </div>
              </div>

              {/* Has Vehicle */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.has_vehicle === '1'}
                    onChange={e => handleInputChange('has_vehicle', e.target.checked ? '1' : '0')}
                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-text-primary">Has Vehicle</span>
                </label>
              </div>
            </div>
          </Card>

          {/* Capture Section */}
          {formData.location_id && cameras.length > 0 && (
            <Card className="mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <CameraIcon className="w-5 h-5" />
                Capture Photos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* ID Capture */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Capture ID
                  </label>
                  <select
                    value={selectedIdCamera}
                    onChange={e => setSelectedIdCamera(e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select ID Camera</option>
                    {cameras
                      .filter(camera => camera.camera_purpose === 'id_capture')
                      .map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location}
                        </option>
                      ))}
                  </select>
                  
                  {selectedIdCamera && (
                    <div className="mt-4">
                      <CameraPhotoCapture key={selectedIdCamera} cameraId={Number(selectedIdCamera)} label="Capture ID Photo"
                        onCapture={file => applyCapture('id', file)} />

                      {capturedIdImage && (
                        <div className="mt-3 relative">
                          <img
                            src={capturedIdImage}
                            alt="Captured ID"
                            className="w-full rounded-lg"
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                          />
                          <button type="button"
                            onClick={() => {
                              setCapturedIdImage(null);
                              setIdPhotoFile(null);
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <p className="text-sm text-green-600 mt-1">✓ ID photo captured</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Face Capture */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Capture Face
                  </label>
                  <select
                    value={selectedFaceCamera}
                    onChange={e => setSelectedFaceCamera(e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select Face Camera</option>
                    {cameras
                      .filter(camera => camera.camera_purpose === 'face_recognition')
                      .map(camera => (
                        <option key={camera.camera_id} value={camera.camera_id}>
                          {camera.name || camera.camera_location}
                        </option>
                      ))}
                  </select>
                  
                  {selectedFaceCamera && (
                    <div className="mt-4">
                      <CameraPhotoCapture key={selectedFaceCamera} cameraId={Number(selectedFaceCamera)} label="Capture Face Photo"
                        onCapture={file => applyCapture('face', file)} />

                      {capturedFaceImage && (
                        <div className="mt-3 relative">
                          <img
                            src={capturedFaceImage}
                            alt="Captured Face"
                            className="w-full rounded-lg"
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                          />
                          <button type="button"
                            onClick={() => {
                              setCapturedFaceImage(null);
                              setFacePhotoFile(null);
                              setRecognitionResult(null);
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <p className="text-sm text-green-600 mt-1">✓ Face photo captured</p>
                          
                          {/* Face Recognition Button */}
                          <Button type="button"
                            onClick={recognizeFace}
                            variant="secondary"
                            className="w-full mt-2"
                            disabled={isRecognizing}
                          >
                            {isRecognizing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Recognizing...
                              </>
                            ) : (
                              <>
                                <Search className="w-4 h-4 mr-2" />
                                Identify Person
                              </>
                            )}
                          </Button>

                          {/* Recognition Result */}
                          {recognitionResult && (
                            <div className="mt-3">
                              {recognitionResult.matched && recognitionResult.matches.length > 0 ? (
                                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                                  <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle size={16} className="text-green-500" />
                                    <span className="text-green-500 font-medium text-sm">
                                      Match Found ({Math.round(recognitionResult.matches[0].confidence * 100)}%)
                                    </span>
                                  </div>
                                  <p className="font-medium text-text-primary">
                                    {recognitionResult.matches[0].person_name}
                                  </p>
                                  <p className="text-sm text-text-muted">
                                    {recognitionResult.matches[0].person_type}
                                    {recognitionResult.matches[0].employee_id && ` • ${recognitionResult.matches[0].employee_id}`}
                                  </p>
                                  {recognitionResult.matches[0].has_access !== undefined && (
                                    <p className={`text-sm mt-1 ${recognitionResult.matches[0].has_access ? 'text-green-500' : 'text-red-500'}`}>
                                      {recognitionResult.matches[0].has_access ? '✓ Has Access' : '✗ No Access'}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <XCircle size={16} className="text-yellow-500" />
                                    <span className="text-yellow-500 font-medium text-sm">No match found</span>
                                  </div>
                                  <p className="text-sm text-text-muted mt-1">Person not registered in system</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* License Plate Capture - Only show if has vehicle */}
                {formData.has_vehicle === '1' && (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Capture License Plate
                    </label>
                    <select
                      value={selectedLicensePlateCamera}
                      onChange={e => setSelectedLicensePlateCamera(e.target.value)}
                      className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                    >
                      <option value="">Select License Plate Camera</option>
                      {cameras
                        .filter(camera => camera.camera_purpose === 'license_plate')
                        .map(camera => (
                          <option key={camera.camera_id} value={camera.camera_id}>
                            {camera.name || camera.camera_location}
                          </option>
                        ))}
                    </select>
                  
                  {selectedLicensePlateCamera && (
                    <div className="mt-4">
                      <CameraPhotoCapture ref={licensePlateCaptureRef} key={selectedLicensePlateCamera} cameraId={Number(selectedLicensePlateCamera)} label="Capture License Plate"
                        onCapture={file => applyCapture('license_plate', file)} />

                      {capturedLicensePlateImage && (
                        <div className="mt-3 relative">
                          <img
                            src={capturedLicensePlateImage}
                            alt="Captured License Plate"
                            className="w-full rounded-lg"
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                          />
                          <button type="button"
                            onClick={() => {
                              setCapturedLicensePlateImage(null);
                              setLicensePlatePhotoFile(null);
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <p className="text-sm text-green-600 mt-1">✓ License plate photo captured</p>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Employee/Visitor Information and Vehicle Information - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left: Employee/Visitor Information */}
            <Card className="lg:col-span-1">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Employee/Visitor Information</h3>
              <div className="space-y-4">
                {/* Name */}
                <Input
                  label="Full Name"
                  placeholder="Employee, Subcontractor, or Visitor Name"
                  value={formData.log_external_driver_name}
                  onChange={e => handleInputChange('log_external_driver_name', e.target.value)}
                />

                {/* Photo ID Number */}
                <Input
                  label="Photo ID Number"
                  placeholder="Driver's License Number"
                  value={formData.log_driver_license_no}
                  onChange={e => handleInputChange('log_driver_license_no', e.target.value)}
                />

                {/* Email */}
                <Input
                  label="Email Address"
                  placeholder="email@example.com"
                  type="email"
                  value={formData.log_driver_email}
                  onChange={e => handleInputChange('log_driver_email', e.target.value)}
                />

                {/* Phone Number */}
                <Input
                  label="Phone Number"
                  placeholder="(123) 456-7890"
                  value={formData.log_visitor_phone_number}
                  onChange={e => handleInputChange('log_visitor_phone_number', e.target.value)}
                />

                {/* Company */}
                <Input
                  label="Company/Organization"
                  placeholder="Company Name"
                  value={formData.log_visitor_company}
                  onChange={e => handleInputChange('log_visitor_company', e.target.value)}
                />

                {/* Purpose */}
                <Input
                  label="Purpose of Visit"
                  placeholder="e.g., Delivery, Meeting, Inspection"
                  value={formData.log_visitor_purpose}
                  onChange={e => handleInputChange('log_visitor_purpose', e.target.value)}
                />

                {/* Safety Vest */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Safety Vest
                  </label>
                  <select
                    value={formData.log_safety_vest}
                    onChange={e => handleInputChange('log_safety_vest', e.target.value)}
                    className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Right: Vehicle Information and Photo */}
            <div className="space-y-6 lg:col-span-2">
              {/* Vehicle Information */}
              {formData.has_vehicle === '1' && (
              <Card>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Information</h3>
                <div className="space-y-4">
                  {/* Vehicle Type */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Vehicle Type
                    </label>
                    <select
                      value={formData.log_vehicle_type}
                      onChange={e => handleInputChange('log_vehicle_type', e.target.value)}
                      className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                    >
                      <option value="">Select Vehicle Type</option>
                      <option value="Car">Car</option>
                      <option value="SUV">SUV</option>
                      <option value="Pickup">Pickup</option>
                      <option value="Truck Only">Truck Only</option>
                      <option value="Truck with Trailer">Truck with Trailer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Two Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Vehicle Make */}
                    <Input
                      label="Vehicle Make"
                      placeholder="e.g., Toyota, Ford, Volvo"
                      value={formData.log_vehicle_make}
                      onChange={e => handleInputChange('log_vehicle_make', e.target.value)}
                    />

                    {/* Vehicle Model */}
                    <Input
                      label="Vehicle Model"
                      placeholder="e.g., Camry, F-150"
                      value={formData.log_vehicle_model}
                      onChange={e => handleInputChange('log_vehicle_model', e.target.value)}
                    />

                    {/* Vehicle Color */}
                    <Input
                      label="Vehicle Color"
                      placeholder="e.g., Red, Blue, Silver"
                      value={formData.log_vehicle_color}
                      onChange={e => handleInputChange('log_vehicle_color', e.target.value)}
                    />

                    {/* License Plate with Lookup */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        License Plate <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="ABC-1234"
                          value={formData.log_license_plate}
                          onChange={e => {
                            handleInputChange('log_license_plate', e.target.value.toUpperCase());
                            setPlateLookupResult(undefined);
                          }}
                          className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-primary/50 bg-surface-secondary text-text-primary ${
                            validationErrors.log_license_plate ? 'border-red-500' : 'border-surface-secondary'
                          }`}
                        />
                        <Button
                          type="button"
                          onClick={lookupPlate}
                          disabled={isLookingUpPlate || !formData.log_license_plate.trim()}
                          variant="secondary"
                          size="sm"
                        >
                          {isLookingUpPlate ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      {validationErrors.log_license_plate && (
                        <p className="text-sm text-red-500 mt-1">{validationErrors.log_license_plate}</p>
                      )}
                      
                      {/* Plate Lookup Result */}
                      {plateLookupResult !== undefined && (
                        <div className="mt-2">
                          {plateLookupResult ? (
                            <div className="p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                              <div className="flex items-center gap-2">
                                <CheckCircle size={14} className="text-green-500" />
                                <span className="text-sm text-green-500 font-medium">
                                  {plateLookupResult.first_name} {plateLookupResult.last_name}
                                </span>
                                <span className="text-xs text-text-muted">({plateLookupResult.person_type})</span>
                              </div>
                            </div>
                          ) : (
                            <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                              <div className="flex items-center gap-2">
                                <XCircle size={14} className="text-yellow-500" />
                                <span className="text-sm text-yellow-500">Plate not registered</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Conditional Truck Number */}
                    {(formData.log_vehicle_type === 'Truck Only' || formData.log_vehicle_type === 'Truck with Trailer') && (
                      <Input
                        label="Truck Number (Unit Number)"
                        placeholder="e.g., T1234"
                        value={formData.log_truck}
                        onChange={e => handleInputChange('log_truck', e.target.value.toUpperCase())}
                      />
                    )}

                    {/* Conditional Trailer Number */}
                    {formData.log_vehicle_type === 'Truck with Trailer' && (
                      <Input
                        label="Trailer Number (Unit Number)"
                        placeholder="e.g., TR5678"
                        value={formData.log_trailer}
                        onChange={e => handleInputChange('log_trailer', e.target.value.toUpperCase())}
                      />
                    )}

                    {/* Conditional Trailer Plate */}
                    {formData.log_vehicle_type === 'Truck with Trailer' && (
                      <Input
                        label="Trailer Plate Number"
                        placeholder="e.g., XYZ789"
                        value={formData.log_trailer_plate}
                        onChange={e => handleInputChange('log_trailer_plate', e.target.value.toUpperCase())}
                      />
                    )}
                  </div>

                  {/* Damages */}
                  <Input
                    label="Vehicle Damages"
                    placeholder="Note any visible damages"
                    value={formData.log_damages}
                    onChange={e => handleInputChange('log_damages', e.target.value)}
                  />

                  {/* Vehicle Inspection Details - 3 Column Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {/* Access Point */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Access Point
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Back Gates, Main Entrance"
                        value={formData.access_point}
                        onChange={e => handleInputChange('access_point', e.target.value)}
                        className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                      />
                    </div>

                    {/* Inspection Status */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Inspection Status
                      </label>
                      <select
                        value={formData.inspection_status}
                        onChange={e => handleInputChange('inspection_status', e.target.value)}
                        className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                      >
                        <option value="">Select Status</option>
                        <option value="Ok">Ok</option>
                        <option value="Needs Attention">Needs Attention</option>
                        <option value="Failed">Failed</option>
                      </select>
                    </div>

                    {/* Inspected By */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Inspected By
                      </label>
                      <input
                        type="text"
                        placeholder="Inspector name"
                        value={formData.inspected_by}
                        onChange={e => handleInputChange('inspected_by', e.target.value)}
                        className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                      />
                    </div>
                  </div>
                </div>
              </Card>
              )}

              {/* Vehicle Photo */}
              {formData.has_vehicle === '1' && (
              <Card>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Vehicle Photos</h3>
                <div className="space-y-4">
                  {/* 4-Angle Capture Buttons */}
                  <div>
                    <p className="text-sm font-medium text-text-primary mb-2">Capture Vehicle Angles</p>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Front */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Front Camera</label>
                        <div className="flex gap-2">
                          <select
                            value={selectedLicensePlateCamera}
                            onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                          >
                            <option value="">Select camera</option>
                            {cameras.filter(c => c.camera_purpose === 'vehicle_front' || c.camera_purpose === 'license_plate').map(camera => (
                              <option key={camera.camera_id} value={camera.camera_id}>
                                {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            aria-label="Capture front vehicle photo"
                            onClick={() => captureVehicleAngle('front')}
                            disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                            variant="outline"
                            size="sm"
                          >
                            {isCapturingVehicleAngle === 'front' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CameraIcon className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Right */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Right Camera</label>
                        <div className="flex gap-2">
                          <select
                            value={selectedLicensePlateCamera}
                            onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                          >
                            <option value="">Select camera</option>
                            {cameras.filter(c => c.camera_purpose === 'vehicle_right' || c.camera_purpose === 'license_plate').map(camera => (
                              <option key={camera.camera_id} value={camera.camera_id}>
                                {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            aria-label="Capture right vehicle photo"
                            onClick={() => captureVehicleAngle('right')}
                            disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                            variant="outline"
                            size="sm"
                          >
                            {isCapturingVehicleAngle === 'right' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CameraIcon className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Left */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Left Camera</label>
                        <div className="flex gap-2">
                          <select
                            value={selectedLicensePlateCamera}
                            onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                          >
                            <option value="">Select camera</option>
                            {cameras.filter(c => c.camera_purpose === 'vehicle_left' || c.camera_purpose === 'license_plate').map(camera => (
                              <option key={camera.camera_id} value={camera.camera_id}>
                                {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            aria-label="Capture left vehicle photo"
                            onClick={() => captureVehicleAngle('left')}
                            disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                            variant="outline"
                            size="sm"
                          >
                            {isCapturingVehicleAngle === 'left' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CameraIcon className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Back */}
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">Back Camera</label>
                        <div className="flex gap-2">
                          <select
                            value={selectedLicensePlateCamera}
                            onChange={(e) => setSelectedLicensePlateCamera(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-background-primary text-gray-900"
                          >
                            <option value="">Select camera</option>
                            {cameras.filter(c => c.camera_purpose === 'vehicle_back' || c.camera_purpose === 'license_plate').map(camera => (
                              <option key={camera.camera_id} value={camera.camera_id}>
                                {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            aria-label="Capture back vehicle photo"
                            onClick={() => captureVehicleAngle('back')}
                            disabled={!selectedLicensePlateCamera || isCapturingVehicleAngle !== null}
                            variant="outline"
                            size="sm"
                          >
                            {isCapturingVehicleAngle === 'back' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CameraIcon className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Show captured angle previews */}
                  {(vehicleFrontImage || vehicleRightImage || vehicleLeftImage || vehicleBackImage) && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {vehicleFrontImage && (
                        <div className="relative">
                          <img src={vehicleFrontImage} alt="Front" className="w-full h-32 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => { setVehicleFrontImage(null); setVehicleFrontFile(null); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                          <p className="text-xs text-center mt-1">Front</p>
                        </div>
                      )}
                      {vehicleRightImage && (
                        <div className="relative">
                          <img src={vehicleRightImage} alt="Right" className="w-full h-32 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => { setVehicleRightImage(null); setVehicleRightFile(null); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                          <p className="text-xs text-center mt-1">Right</p>
                        </div>
                      )}
                      {vehicleLeftImage && (
                        <div className="relative">
                          <img src={vehicleLeftImage} alt="Left" className="w-full h-32 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => { setVehicleLeftImage(null); setVehicleLeftFile(null); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                          <p className="text-xs text-center mt-1">Left</p>
                        </div>
                      )}
                      {vehicleBackImage && (
                        <div className="relative">
                          <img src={vehicleBackImage} alt="Back" className="w-full h-32 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => { setVehicleBackImage(null); setVehicleBackFile(null); }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                          <p className="text-xs text-center mt-1">Back</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-t border-border-primary my-4"></div>

                  {/* Manual Upload Option */}
                  <div>
                    <p className="text-sm font-medium text-text-primary mb-2">Or Upload Manually</p>
                    {photoPreview ? (
                      <div className="relative inline-block">
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="w-full max-h-60 object-contain rounded-lg border border-border-primary"
                        />
                        <button
                          type="button"
                          onClick={removePhoto}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-border-primary rounded-lg cursor-pointer hover:bg-gray-50">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload size={40} className="text-text-muted mb-3" />
                          <p className="text-sm text-text-primary font-medium mb-1">
                            Click to upload vehicle photo
                          </p>
                          <p className="text-xs text-text-muted">
                            JPG, PNG or WEBP (Max 5MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handlePhotoChange}
                          className="hidden"
                        />
                      </label>
                    )}
                    {validationErrors.photo && (
                      <p className="text-sm text-red-500">{validationErrors.photo}</p>
                    )}
                  </div>
                </div>
              </Card>
              )}

              {/* Parking Assignment - Show for ENTRY/EXIT with vehicle when location is selected */}
              {formData.has_vehicle === '1' && formData.location_id ? (
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <ParkingCircle size={20} className="text-primary-500" />
                  <h3 className="text-lg font-semibold text-text-primary">
                    {formData.log_access_type === 'EXIT' ? 'Parking Release' : 'Parking Assignment'}
                  </h3>
                </div>
                
                {loadingParkingLots ? (
                  <p className="text-sm text-text-muted">Loading parking lots...</p>
                ) : parkingLots.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      No parking lots configured for this location. 
                      <span className="block mt-1 text-yellow-600">Contact administrator to set up parking lots.</span>
                    </p>
                  </div>
                ) : (
                <div className="space-y-4">
                  {/* Parking Lot Selection */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Parking Lot
                    </label>
                    <select
                      value={selectedParkingLot}
                      onChange={e => handleParkingLotChange(e.target.value)}
                      disabled={loadingParkingLots}
                      className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                    >
                      <option value="">Select a parking lot</option>
                      {parkingLots.map(lot => (
                        <option key={lot.id} value={lot.id}>
                          {lot.name} ({lot.available_spots}/{lot.total_spots} available)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Parking Summary */}
                  {selectedParkingLot && parkingSummary && (
                    <div className={`border rounded-lg p-3 ${formData.log_access_type === 'EXIT' ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
                      <p className={`text-sm ${formData.log_access_type === 'EXIT' ? 'text-orange-800' : 'text-blue-800'}`}>
                        {formData.log_access_type === 'EXIT' ? (
                          <>
                            <span className="font-medium">{parkingSummary.total - parkingSummary.available}</span> occupied spots available for release
                          </>
                        ) : (
                          <>
                            <span className="font-medium">{parkingSummary.available}</span> of{' '}
                            <span className="font-medium">{parkingSummary.total}</span> spots available
                            {formData.log_vehicle_type && (
                              <span className="ml-2">
                                (filtered for {formData.log_vehicle_type.toLowerCase()})
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Parking Spot Selection */}
                  {selectedParkingLot && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {formData.log_access_type === 'EXIT' ? 'Select Spot to Release' : (formData.log_vehicle_type === 'Truck with Trailer' ? 'Truck Spot #' : 'Parking Spot')}
                      </label>
                      {loadingSpots ? (
                        <p className="text-sm text-text-muted">
                          {formData.log_access_type === 'EXIT' ? 'Loading occupied spots...' : 'Loading available spots...'}
                        </p>
                      ) : availableSpots.length === 0 ? (
                        <p className="text-sm text-orange-600">
                          {formData.log_access_type === 'EXIT' 
                            ? 'No occupied spots found in this lot.' 
                            : 'No available spots in this lot for the selected vehicle type.'}
                        </p>
                      ) : (
                        <select
                          value={selectedParkingSpot}
                          onChange={e => handleParkingSpotChange(e.target.value)}
                          className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                        >
                          <option value="">
                            {formData.log_access_type === 'EXIT' ? 'Select spot to release' : 'Select a spot'}
                          </option>
                          {availableSpots.map(spot => {
                            const spotId = (spot as any).id || spot.spot_id;
                            const spotSection = (spot as any).row || spot.section;
                            const vehiclePlate = (spot as any).current_vehicle_plate;
                            return (
                              <option key={spotId} value={spotId} disabled={spotId?.toString() === selectedParkingSpot2}>
                                {spot.spot_number} - {spot.spot_type}
                                {spotSection && ` (${spotSection})`}
                                {spot.floor && ` - Floor ${spot.floor}`}
                                {vehiclePlate && ` - ${vehiclePlate}`}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Second Parking Spot Selection - Only for Truck with Trailer */}
                  {selectedParkingLot && formData.log_vehicle_type === 'Truck with Trailer' && selectedParkingSpot && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {formData.log_access_type === 'EXIT' ? 'Trailer Spot to Release (Optional)' : 'Trailer Spot # (Optional)'}
                      </label>
                      <p className="text-xs text-text-muted mb-2">
                        Select a second spot for the trailer if needed.
                      </p>
                      {loadingSpots ? (
                        <p className="text-sm text-text-muted">Loading spots...</p>
                      ) : (
                        <select
                          value={selectedParkingSpot2}
                          onChange={e => setSelectedParkingSpot2(e.target.value)}
                          className="w-full px-3 py-2 border border-border-primary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-background-primary text-gray-900"
                        >
                          <option value="">No second spot needed</option>
                          {availableSpots
                            .filter(spot => {
                              const spotId = (spot as any).id || spot.spot_id;
                              return spotId?.toString() !== selectedParkingSpot; // Exclude first selected spot
                            })
                            .map(spot => {
                              const spotId = (spot as any).id || spot.spot_id;
                              const spotSection = (spot as any).row || spot.section;
                              const vehiclePlate = (spot as any).current_vehicle_plate;
                              return (
                                <option key={spotId} value={spotId}>
                                  {spot.spot_number} - {spot.spot_type}
                                  {spotSection && ` (${spotSection})`}
                                  {spot.floor && ` - Floor ${spot.floor}`}
                                  {vehiclePlate && ` - ${vehiclePlate}`}
                                </option>
                              );
                            })}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Selected Spot Info */}
                  {selectedParkingSpot && (
                    <div className={`border rounded-lg p-3 ${formData.log_access_type === 'EXIT' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                      {(() => {
                        const spot = availableSpots.find(s => {
                          const sId = (s as any).id || s.spot_id;
                          return sId?.toString() === selectedParkingSpot;
                        });
                        const spot2 = selectedParkingSpot2 ? availableSpots.find(s => {
                          const sId = (s as any).id || s.spot_id;
                          return sId?.toString() === selectedParkingSpot2;
                        }) : null;
                        if (!spot) return null;
                        const spotSection = (spot as any).row || spot.section;
                        const vehiclePlate = (spot as any).current_vehicle_plate;
                        const isTruckWithTrailer = formData.log_vehicle_type === 'Truck with Trailer';
                        return (
                          <div className={`text-sm ${formData.log_access_type === 'EXIT' ? 'text-orange-800' : 'text-green-800'}`}>
                            {formData.log_access_type === 'EXIT' ? (
                              <>
                                <p><span className="font-medium">{isTruckWithTrailer ? 'Truck Spot:' : 'To Release:'}</span> {spot.spot_number}
                                {spotSection && ` in ${spotSection}`}
                                {vehiclePlate && ` - Vehicle: ${vehiclePlate}`}</p>
                                {spot2 && (
                                  <p className="mt-1"><span className="font-medium">Trailer Spot:</span> {spot2.spot_number}</p>
                                )}
                              </>
                            ) : (
                              <>
                                <p><span className="font-medium">{isTruckWithTrailer ? 'Truck Spot:' : 'Selected:'}</span> {spot.spot_number}
                                {spotSection && ` in ${spotSection}`}
                                {spot.floor && ` (Floor ${spot.floor})`}
                                {' - '}{spot.spot_type} spot</p>
                                {spot2 && (
                                  <p className="mt-1"><span className="font-medium">Trailer Spot:</span> {spot2.spot_number} - {spot2.spot_type} spot</p>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
              </Card>
              ) : (
                <Card className="bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <ParkingCircle size={20} className="text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-400">Parking Assignment</h3>
                  </div>
                  <p className="text-sm text-gray-500">Select a location to enable parking assignment.</p>
                </Card>
              )}
              
              {/* Show placeholder when no vehicle */}
              {formData.has_vehicle !== '1' && (
                <Card className="bg-gray-50 border-dashed border-gray-300">
                  <div className="flex items-center gap-2 mb-2">
                    <ParkingCircle size={20} className="text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-400">Parking Assignment</h3>
                  </div>
                  <p className="text-sm text-gray-400">Enable "Has Vehicle" to assign parking.</p>
                </Card>
              )}
            </div>
          </div>

          {/* Notes */}
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Additional Notes</h3>
            <textarea
              value={formData.log_note}
              onChange={e => handleInputChange('log_note', e.target.value)}
              placeholder="Enter any additional notes..."
              rows={4}
              className={`w-full px-4 py-2 rounded-lg border ${
                validationErrors.log_note ? 'border-red-500' : 'border-border-primary'
              } bg-background-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
            <div className="flex justify-between items-center mt-2">
              {validationErrors.log_note && (
                <p className="text-sm text-red-500">{validationErrors.log_note}</p>
              )}
              <p className="text-sm text-text-muted ml-auto">
                {formData.log_note.length}/2000 characters
              </p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/entry-exit')}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <Loading 
                  size="sm" 
                  text={uploadingImages ? uploadProgress : 'Creating...'} 
                />
              ) : (
                'Create Log'
              )}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default CreateEntryExitPage;
